'use client'

import { useMemo } from 'react'
import { usePathname, useRouter } from 'next/navigation'

import { DropdownMenu, Icon, IconComponent, type MenuItem } from '@graftcode/design-system'

import { formatQuickStartLanguageLabel } from '@common/utils/formatQuickStartLanguageLabel'

import { quickStartNavUrlPathForSection, resolveQuickStartSlug } from '@/lib/github/tutorialSlug'
import { getQuickStartLanguageIcon } from '@/shared/components/QuickStartLanguageTabs/utils/getQuickStartLanguageIcon'
import { useQuickStartLanguageContext } from '@/shared/components/QuickStartLayoutShell/context/quickStartLanguageContext'
import { ACADEMY_ROUTES } from '@/shared/docs-shared.const'
import { QUICK_START_ARTICLE_SELECTORS_TEST_IDS } from '@/shared/docs-test-ids.const'

import styles from './QuickStartArticleSelectors.module.scss'

export function QuickStartArticleSelectors() {
  const { sections, selectedLanguage, setSelectedLanguage } = useQuickStartLanguageContext()
  const pathname = usePathname()
  const router = useRouter()

  const currentSection = useMemo(() => {
    const prefix = `${ACADEMY_ROUTES.quickStart}/`
    if (!pathname?.startsWith(prefix)) return null
    const slugParts = pathname.slice(prefix.length).split('/').filter(Boolean)
    const resolved = resolveQuickStartSlug(slugParts, sections)
    return sections.find((s) => s.path === resolved?.articlePath) ?? null
  }, [pathname, sections])

  const displayLanguages = useMemo(() => {
    const langs = currentSection?.languages ?? []
    const real = langs.filter((l) => l !== 'default')
    return real.length > 0 ? real : langs.length > 0 ? langs : ['default']
  }, [currentSection])

  const handleLanguageClick = (lang: string) => {
    setSelectedLanguage(lang)
    if (currentSection) {
      router.push(`${ACADEMY_ROUTES.quickStart}/${quickStartNavUrlPathForSection(currentSection, lang)}`, {
        scroll: false,
      })
    }
  }

  const languageItems = useMemo<MenuItem[]>(
    () =>
      displayLanguages.map((lang) => ({
        type: 'item',
        id: lang,
        label: formatQuickStartLanguageLabel(lang),
        icon: getQuickStartLanguageIcon(lang),
        iconSize: 20,
        active: true,
        groups: [],
        selected: lang === selectedLanguage,
      })),
    [displayLanguages, selectedLanguage]
  )

  const activeLang =
    selectedLanguage && displayLanguages.includes(selectedLanguage)
      ? selectedLanguage
      : (displayLanguages[0] ?? 'default')

  const hasTechnologyChoice = displayLanguages.some((l) => l !== 'default')

  if (!pathname?.startsWith(`${ACADEMY_ROUTES.quickStart}/`)) return null

  if (!hasTechnologyChoice) return null

  return (
    <div data-testid={QUICK_START_ARTICLE_SELECTORS_TEST_IDS.root} className={styles.wrap}>
      <div className={styles.selectorGroup}>
        <span className={styles.label}>Your stack:</span>
        <DropdownMenu
          width="s"
          align="start"
          side="bottom"
          sideOffset={8}
          items={languageItems}
          onItemClick={handleLanguageClick}
        >
          <button type="button" className={styles.trigger} aria-label="Select technology stack">
            <IconComponent
              icon={getQuickStartLanguageIcon(activeLang)}
              size={24}
              className={styles.triggerIcon}
              aria-hidden
            />
            <span className={styles.triggerText}>{formatQuickStartLanguageLabel(activeLang)}</span>
            <IconComponent icon={Icon.ChevronDownRegular} size={24} className={styles.chevron} aria-hidden />
          </button>
        </DropdownMenu>
      </div>
    </div>
  )
}
